import { useCallback, useEffect } from 'react';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { useBookAppointmentStore } from '../store';
import { useUserQuery } from '../graphql/types-and-hooks';

/**
 * Custom hook to handle booking flow logic and state
 * 
 * @param isOpen Whether the modal is open
 * @param onClose Function to close the modal
 */
export const useBookingFlow = (isOpen, onClose) => {
  // Get data from global stores
  const [patient, doctor] = useBookAppointmentStore(
    (state) => [state.patient, state.doctor, state.setData]
  );

  // User data for authentication status
  const { data: userData } = useUserQuery({ variables: {} });
  const user = userData?.user;
  const isLoggedIn = !!user;
  
  // Get values from booking flow store
  const {
    appointmentData,
    activeStep,
    currentStep,
    appointmentDetails
  } = servicebookingFlowStore;
  
  // Extract selected doctor and time from appointment data
  const selectedDoctor = appointmentData.selectedDoctor || doctor;
  const selectedTime = appointmentData.selectedTime;
  
  // Initialize the booking flow when the modal opens
  useEffect(() => {
    if (isOpen && doctor) {
      // If a doctor is pre-selected, update the store
      servicebookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
    }
  }, [isOpen, doctor]);
  
  // Close modal handler
  const handleCloseModal = useCallback(() => {
    servicebookingFlowStore.setIsModalOpen(false);
    servicebookingFlowStore.resetFlow();
    onClose();
  }, [onClose]);
  
  // Back button handler
  const handleBackClick = useCallback(() => {
    servicebookingFlowStore.handleBackClick();
  }, []);
  
  // Login navigation handler
  const goToLogin = useCallback(() => {
    servicebookingFlowStore.goToLogin();
    servicebookingFlowStore.setActiveStep(8);
  }, []);

  return {
    // Flow state
    appointmentData,
    activeStep,
    currentStep,
    appointmentDetails,
    
    // User and patient data
    user,
    isLoggedIn,
    patient,
    
    // Selection data
    selectedDoctor,
    selectedTime,
    
    // Action handlers
    handleCloseModal,
    handleBackClick,
    goToLogin
  };
};